import {Command, Connection, Data, WsMeshServer} from './types.js';
import {WebsocketConnection, WebsocketConnectionFactory} from '../../common/ws/types.js';

export type ServerCallbacks = {
    onConnect: (source: string, target: string, connection: Connection) => void
    onDisconnect: (source: string, target: string) => void;
    onReceive: (target: string, source: string, msg: Data['data']) => void
}

type NodeConnection = {
    readonly from: string;
    readonly to: string,
    readonly connection: Connection
}
function ensureConnection(from: string, to: string, connected: Map<string, NodeConnection>, relayConnection: WebsocketConnection<Command>) {
    const k = `${from}:${to}`;
    const c = connected.get(k);
    if (c) {
        return c.connection;
    }
    const connection = new class implements Connection {
        close(): void {
            relayConnection.send({type: 'bye', from: to, to: from});
            connected.delete(k);
        }

        send(cmd: Command): void {
            relayConnection.send(cmd);
        }

        toJSON() {
            return {type: 'relay', uri: relayConnection.uri};
        }
    };
    connected.set(k, {from, to , connection});
    return connection;
}

function processMessage(connected: Map<string, NodeConnection>, callbacks: ServerCallbacks, relayConnection: WebsocketConnection<Command>, msg: Command) {
    switch (msg.type) {
        case 'hello': {
            const { from, to } = msg;
            const connection = ensureConnection(from, to, connected, relayConnection);
            callbacks.onConnect(to, from, connection);
            break;
        }
        case 'bye': {
            const { from, to } = msg;
            callbacks.onDisconnect(to, from);
            break;
        }
        case 'data': {
            const { from, to, data } = msg;
            callbacks.onReceive(to, from, data);
            break;
        }
    }
}

export function createRelay(factory: WebsocketConnectionFactory<Command>, endpoint: string, callbacks: ServerCallbacks): WsMeshServer {
    const nodes = new Set<string>();
    const connected = new Map<string, NodeConnection>();
    const relayConnection = factory.create(endpoint);
    relayConnection.onConnect = () => {
        nodes.forEach(node => relayConnection.send({type: 'hello', from: node, to: 'all'}));
    };
    relayConnection.onReceive = (msg) => {
        processMessage(connected, callbacks, relayConnection, msg);
    };
    relayConnection.onDisconnect = () => {
        for (const [, c] of connected) {
            c.connection.close();
            callbacks.onDisconnect(c.to, c.from);
        }
    };
    relayConnection.connect();
    return {
        register: (node: string) => {
            nodes.add(node);
        },
        unregister: (node: string) => {
            nodes.delete(node);
        },
        stop: () => {
            relayConnection.close();
        }
    };
}
